import {
  Button,
  Checkbox,
  Divider,
  Flex,
  Text,
  Tooltip,
  useMantineTheme,
} from "@mantine/core";
import { useDisclosure, useMediaQuery } from "@mantine/hooks";
import { Receipt, Trash } from "iconsax-react";
import { useAuth } from "../../context/auth";
import { TagCapsules, Drawer } from "../../components";
import { useLocation, useNavigate } from "react-router-dom";
import { useCallback } from "react";
import { formatCurrency } from "../../utils/currency";
import { IColor } from "../../utils/selectColors";
import { useTranslation } from "react-i18next";
import {
  tradutorCategory,
  tradutorSubcategory,
} from "../../utils/SubCategorys";

const InterestDrawer = () => {
  const theme = useMantineTheme();
  const [opened, { open, close }] = useDisclosure(false);
  const { interest, setInterest, isAuthenticated } = useAuth();
  const hasInterest = !!interest && interest.length > 0;
  const navigate = useNavigate();
  const location = useLocation();
  const isLargerThan991 = useMediaQuery("(min-width: 991px)");
  const hasSelected = hasInterest && interest.some((item) => item.selected);
  const { t } = useTranslation();
  const handleToggle = (index: number) => {
    setInterest((prev) =>
      prev.map((item, i) =>
        i === index ? { ...item, selected: !item.selected } : item
      )
    );
  };

  const handleDelete = (index: number) => {
    setInterest((prev) => prev.filter((_, i) => i !== index));
    if (interest.length === 0) close();
  };

  const handleRedirect = useCallback(() => {
    const selectedItems =
      hasInterest && interest.filter((item) => item.selected);
    const from = location.pathname;
    const buyProducts = "/comprar-servicos";

    if (isAuthenticated) {
      navigate(buyProducts, {
        state: {
          from,
          selectedItems,
        },
      });
    } else {
      navigate("/autenticacao/acessar-conta", {
        state: {
          from: "/marketplace",
          selectedItems,
        },
      });
    }
    close();
  }, [location.pathname, navigate, interest, isAuthenticated]);

  return (
    <>
      {hasInterest ? (
        <Drawer
          opened={opened}
          onClose={close}
          title="Lista de Interesse"
          childrenFooter={
            <Button
              onClick={handleRedirect}
              w="100%"
              variant="primary"
              disabled={!hasSelected}
            >
              {isAuthenticated
                ? t("global.navbar.interest_drawer.button_is_authenticated")
                : t(
                    "global.navbar.interest_drawer.button_is_not_authenticated"
                  )}
            </Button>
          }
          heightBody={
            isLargerThan991
              ? "calc(100vdh - 132px - 152px)"
              : "calc(100vdh - 72px - 103px)"
          }
        >
          <Flex
            direction="column"
            gap={32}
            p={{ base: "24px", 9991: "24px 48px" }}
          >
            <Text fs={"20px"} fw={600} color={theme.colors.basic[4]}>
              {t("global.navbar.interest_drawer.select_all_itens")}
            </Text>
            {interest?.map((item, index) => (
              <Flex gap={32} key={index} w="100%" direction="column">
                {index !== 0 && <Divider />}
                <Flex align="center" justify="space-between" w="100%">
                  <Flex align="center" gap={16} style={{ flex: 1 }}>
                    <Checkbox
                      checked={item.selected || false}
                      onChange={() => handleToggle(index)}
                    />
                    <Flex
                      align="start"
                      gap={16}
                      direction={{ base: "column", 520: "row" }}
                      w="100%"
                    >
                      <Flex
                        w={{ base: "50px", 991: "100px" }}
                        h={{ base: "50px", 9991: "100px" }}
                        sx={{
                          backgroundImage: `url(${item?.product?.icon})`,
                          backgroundSize: "cover",
                          backgroundPosition: "center",
                          borderRadius: "12px",
                        }}
                      />
                      <Flex direction="column" gap={8} style={{ flex: 1 }}>
                        <Flex direction="column" gap={4}>
                          <TagCapsules colorTag={IColor.orange} border={true}>
                            {t(tradutorCategory(item?.product?.category).title)}
                          </TagCapsules>
                          {item?.product?.subcategory ? (
                            <TagCapsules colorTag={IColor.yellow} border={true}>
                              {t(
                                tradutorSubcategory(item?.product?.subcategory)
                                  .title
                              )}
                            </TagCapsules>
                          ) : null}
                          <Text
                            color={theme?.colors?.basic[5]}
                            size="lg"
                            weight={700}
                          >
                            {item?.product?.title}
                          </Text>
                        </Flex>

                        <Flex direction="column" gap={4}>
                          <Flex align="center" gap={4}>
                            <Text color={theme?.colors.basic[4]} size="sm">
                              {t("global.navbar.interest_drawer.sell_by")}
                            </Text>
                            <Text
                              color={theme?.colors.basic[5]}
                              size="sm"
                              weight={600}
                            >
                              {item?.product?.partner?.name}
                            </Text>
                          </Flex>
                          <Flex align="center" gap={4}>
                            <Text color={theme.colors.basic[4]} size="sm">
                              {t("global.navbar.interest_drawer.value")}
                            </Text>
                            <Text
                              color={theme.colors.basic[5]}
                              size="sm"
                              weight={600}
                            >
                              {t("global.navbar.interest_drawer.start_by")}
                              {formatCurrency(
                                Math.min(...item?.plan.map((p) => p.price))
                              )}
                            </Text>
                          </Flex>
                        </Flex>
                      </Flex>
                    </Flex>
                  </Flex>
                  <Button
                    variant="subtle"
                    style={{ padding: 8, borderRadius: 8 }}
                    onClick={() => handleDelete(index)}
                  >
                    <Trash size="32" color={theme.colors.basic[4]} />
                  </Button>
                </Flex>
              </Flex>
            ))}
          </Flex>
        </Drawer>
      ) : null}

      <Flex h={{ base: 48, 991: 56 }} w="fit-content" align="center" gap={10}>
        <Tooltip
          label={t("global.navbar.interest_drawer.start_by", {
            empty: hasInterest ? "" : "vazia",
          })}
          color={theme.colors.basic[5]}
          position="bottom"
          withArrow
        >
          <Button
            variant="transparentEspecial"
            onClick={open}
            h={{ base: 48, 991: 56 }}
            p={{ base: 8, 991: 12 }}
            leftIcon={
              <Receipt
                size={24}
                variant="Outline"
                color={theme.colors.basic[0]}
              />
            }
            styles={{
              leftIcon: {
                marginRight: hasInterest ? "10px" : 0,
                "@media (max-width: 991px)": {
                  marginRight: hasInterest ? "4px" : 0,
                },
              },
            }}
          >
            {hasInterest ? (
              <Flex
                bg={theme.colors.primary[1]}
                p="4px 8px"
                align="center"
                justify="center"
                style={{
                  borderRadius: "20px",
                  color: theme.colors.basic[0],
                  fontSize: "14px",
                  fontWeight: 600,
                  lineHeight: "22px",
                }}
              >
                {interest.length > 9 ? "+9" : `0${interest.length}`}
              </Flex>
            ) : null}
          </Button>
        </Tooltip>
      </Flex>
    </>
  );
};

export default InterestDrawer;
